/**
 * Adapted from https://github.com/DavidWells/analytics/blob/master/packages/analytics-util-queue/src/index.js
 * but modified to add comments and TypeScript types.
 */

/**
 * Handler function type that processes a batch of items from the queue.
 * @typeParam T - The type of items in the queue.
 * @param removed - The items removed from the queue.
 * @param queue - The remaining items in the queue.
 */
type Handler<T> = (removed: T[], queue: T[]) => void;

/**
 * No-operation function type.
 */
type NoOp = () => void;

/**
 * Options for configuring the smartQueue function.
 * @typeParam T - The type of items in the queue.
 */
interface SmartQueueOptions<T> {
  /** Initial items in the queue. */
  initial?: T[];
  /** Maximum number of items to process at once. */
  max?: number;
  /** Interval for processing the queue, in milliseconds. */
  interval?: number;
  /** Callback when the queue is empty. */
  onEmpty?: (queue: T[]) => void;
  /** Callback when the queue is paused. */
  onPause?: (queue: T[]) => void;
  /** Whether to throttle the queue processing. */
  throttle?: boolean;
}

/**
 * Default no-operation function.
 */
const noOp: NoOp = () => {};

/**
 * Creates a smart queue for managing and processing items.
 * @typeParam T - The type of items in the queue.
 * @param handler - Function to process the queue items.
 * @param opts - Configuration options for the queue.
 * @returns An object with methods to control the queue.
 */
export function smartQueue<T>(handler: Handler<T>, opts: SmartQueueOptions<T> = {}) {
  let timer: NodeJS.Timeout | undefined;
  let tmp: number;
  let running: boolean;
  const queue: T[] = opts.initial || [];
  const max: number = opts.max || Infinity;
  const int: number = opts.interval || 10e3;
  const onEmpty: (queue: T[]) => void = opts.onEmpty || noOp;
  const onPause: (queue: T[]) => void = opts.onPause || noOp;

  /**
   * Processes a batch of items from the queue.
   * @param all - If true, processes all items immediately.
   */
  function batch(all?: boolean): void {
    clearInterval(timer);
    const removed = queue.splice(0, max);
    if (removed.length) {
      handler(removed, queue);
    }
    if (!queue.length) {
      running = false;
      return onEmpty(queue);
    }
    if (all) {
      return batch();
    }
    return ticker();
  }

  /**
   * Starts the interval for processing the queue.
   */
  function ticker(): void {
    running = true;
    timer = setInterval(batch, int);
  }

  if (queue.length) {
    ticker();
  }

  return {
    /**
     * Flushes the queue, optionally processing all items.
     * @param all - If true, processes all items immediately.
     */
    flush: (all?: boolean): void => {
      batch(all);
    },
    /**
     * Resumes processing the queue.
     */
    resume: (): void => {
      batch();
    },
    /**
     * Adds an item to the queue.
     * @param val - The item to add to the queue.
     * @returns The new length of the queue.
     */
    push: (val: T): number => {
      tmp = queue.push(val);
      if (tmp >= max && !opts.throttle) {
        batch();
      }
      if (!running) {
        ticker();
      }
      return tmp;
    },
    /**
     * Gets the current size of the queue.
     * @returns The length of the queue.
     */
    size: (): number => {
      return queue.length;
    },
    /**
     * Pauses processing the queue, optionally flushing remaining items.
     * @param toFlush - If true, processes remaining items before pausing.
     */
    pause: (toFlush?: boolean): void => {
      if (toFlush) batch();
      clearInterval(timer);
      running = false;
      onPause(queue);
    },
  };
}
